import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';

/**
 * Searchable client picker — replaces the basic <Select> in forms when the
 * client list grows past ~10 entries. Uses cmdk under the hood so typing
 * filters by name (case-insensitive) and matches anywhere in the string.
 */
const ClientsCombobox = ({
  clients = [],
  value,
  onChange,
  placeholder = 'Select client',
  disabled = false,
  testid = 'clients-combobox',
  /** When true, prepends an "All Clients" option (sentinel value: 'all') */
  includeAll = false,
  allLabel = 'All Clients',
}) => {
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value);
  const isAllSelected = includeAll && (value === 'all' || !value);
  const triggerLabel = isAllSelected
    ? allLabel
    : selected
    ? selected.name
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          data-testid={testid}
          className="flex items-center gap-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-800 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className={`truncate flex-1 text-left ${selected || isAllSelected ? 'text-slate-800' : 'text-slate-400'}`}>
            {triggerLabel}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[280px] p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <Command>
          <CommandInput
            placeholder="Search client by name..."
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No clients match that search.</CommandEmpty>
            {includeAll ? (
              <CommandGroup>
                <CommandItem
                  value="all"
                  keywords={["all", "every", "everything", "*"]}
                  onSelect={() => {
                    onChange?.('all');
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-all`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Check
                    className={`w-3.5 h-3.5 flex-shrink-0 ${
                      isAllSelected ? 'text-teal-600' : 'opacity-0'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{allLabel}</div>
                    <div className="text-[11px] text-slate-500">Show jobs across every client</div>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{clients.length}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            <CommandGroup heading={includeAll ? 'Clients' : undefined}>
              {clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  keywords={[c.name || '', c.industry || '', c.contact_person || '']}
                  onSelect={() => {
                    onChange?.(c.id);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${c.id}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Check
                    className={`w-3.5 h-3.5 flex-shrink-0 ${
                      value === c.id ? 'text-teal-600' : 'opacity-0'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate" title={c.name}>
                      {c.name}
                    </div>
                    {c.industry ? (
                      <div className="text-[11px] text-slate-500 truncate">{c.industry}</div>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ClientsCombobox;
